// Schematic release-note illustrations as data. Every shape names a tone, never
// a color: ReleaseNoteIllustration maps tones to the company theme, so each
// illustration follows the company's brand colors. No text, so one illustration
// serves every language. Canvas: 640 × 360 (16:9).

export type IllustrationTone =
  | 'surface'
  | 'line'
  | 'muted'
  | 'subtle'
  | 'accent'
  | 'accentMild'
  | 'accentSoft'
  // The company's first three culture value colors
  | 'culture1'
  | 'culture2'
  | 'culture3'

export type IllustrationShape =
  | {
      type: 'rect'
      x: number
      y: number
      w: number
      h: number
      r?: number
      fill?: IllustrationTone
      stroke?: IllustrationTone
      strokeWidth?: number
      rotate?: number
      lift?: boolean
    }
  | {
      type: 'circle'
      cx: number
      cy: number
      r: number
      fill?: IllustrationTone
      stroke?: IllustrationTone
      strokeWidth?: number
      lift?: boolean
    }
  | {
      type: 'path'
      d: string
      fill?: IllustrationTone
      stroke?: IllustrationTone
      strokeWidth?: number
      lift?: boolean
    }

// Building blocks, so every illustration shares one visual language
const card = (
  x: number,
  y: number,
  w: number,
  h: number,
  emphasis = false
): IllustrationShape => ({
  type: 'rect',
  x,
  y,
  w,
  h,
  r: 12,
  fill: 'surface',
  stroke: emphasis ? 'accent' : 'line',
  strokeWidth: emphasis ? 2 : 1.5,
})

const bar = (
  x: number,
  y: number,
  w: number,
  fill: IllustrationTone = 'muted',
  h = 8
): IllustrationShape => ({ type: 'rect', x, y, w, h, r: h / 2, fill })

const chip = (
  x: number,
  y: number,
  w: number,
  fill: IllustrationTone = 'accentSoft'
): IllustrationShape => ({ type: 'rect', x, y, w, h: 16, r: 8, fill })

const circle = (
  cx: number,
  cy: number,
  r: number,
  fill: IllustrationTone = 'accentMild'
): IllustrationShape => ({ type: 'circle', cx, cy, r, fill })

// Overlapping avatars with a surface ring
const avatars = (x: number, y: number, count: number): IllustrationShape[] =>
  Array.from({ length: count }, (_, i) => ({
    type: 'circle' as const,
    cx: x + i * 20,
    cy: y,
    r: 12,
    fill: ([
      'accentMild',
      'culture1',
      'culture2',
      'culture3',
    ] as IllustrationTone[])[i % 4],
    stroke: 'surface' as const,
    strokeWidth: 3,
  }))

// The one object an illustration puts forward, raised with a drop shadow
const lifted = (shape: IllustrationShape): IllustrationShape => ({
  ...shape,
  lift: true,
})

const connector = (d: string): IllustrationShape => ({
  type: 'path',
  d,
  stroke: 'accentMild',
  strokeWidth: 2,
})

const releaseNoteIllustrations: Record<string, IllustrationShape[]> = {
  // Jobs stepping up a growth path, each with its own skills
  jobsUpdate: [
    connector('M216 236 C232 236 228 216 244 216'),
    connector('M396 196 C412 196 408 176 424 176'),
    card(64, 176, 152, 120),
    bar(80, 196, 88, 'subtle', 12),
    chip(80, 224, 48),
    chip(136, 224, 56, 'muted'),
    bar(80, 256, 120),
    bar(80, 272, 88),
    lifted(card(244, 136, 152, 160, true)),
    bar(260, 156, 96, 'subtle', 12),
    chip(260, 184, 56),
    chip(324, 184, 48),
    bar(260, 216, 120),
    bar(260, 232, 96),
    ...avatars(272, 268, 3),
    card(424, 96, 152, 200),
    bar(440, 116, 104, 'subtle', 12),
    chip(440, 144, 48),
    chip(496, 144, 56, 'muted'),
    chip(440, 168, 64, 'muted'),
    bar(440, 200, 120),
    bar(440, 216, 104),
    bar(440, 232, 80),
    ...avatars(452, 268, 2),
  ],

  // A completed assessment opened up again
  reopenAssessments: [
    card(176, 56, 288, 248),
    circle(216, 100, 20),
    bar(248, 88, 120, 'subtle', 12),
    bar(248, 108, 72),
    ...[152, 188, 224, 260].flatMap((y, i): IllustrationShape[] => [
      circle(212, y, 10, i < 2 ? 'accentSoft' : 'muted'),
      ...(i < 2
        ? [
            {
              type: 'path' as const,
              d: `M207 ${y} l4 4 l7 -8`,
              stroke: 'accent' as const,
              strokeWidth: 2,
            },
          ]
        : []),
      bar(236, y - 4, [168, 128, 152, 112][i]),
    ]),
    lifted(circle(456, 72, 32, 'accent')),
    {
      type: 'path',
      d: 'M468 72 A12 12 0 1 1 460 60.7',
      stroke: 'surface',
      strokeWidth: 3,
    },
    {
      type: 'path',
      d: 'M454 54 L462 60 L456 68',
      stroke: 'surface',
      strokeWidth: 3,
    },
  ],

  // Three progress blocks for one team
  teamProgress: [
    ...[80, 248, 416].flatMap((x, i): IllustrationShape[] => [
      i === 0 ? lifted(card(x, 88, 144, 184, true)) : card(x, 88, 144, 184),
      bar(x + 16, 108, 72, 'subtle', 12),
      bar(x + 16, 148, 112, 'muted', 12),
      bar(x + 16, 148, [80, 56, 96][i], 'accent', 12),
      bar(x + 16, 180, 96),
      bar(x + 16, 196, 72),
      ...avatars(x + 28, 240, 3),
    ]),
  ],

  // A job profile with properties, shared as exports
  jobSetupExports: [
    card(72, 56, 280, 248),
    { type: 'rect', x: 72, y: 56, w: 280, h: 72, r: 12, fill: 'accentSoft' },
    circle(112, 92, 16, 'accent'),
    bar(140, 84, 128, 'subtle', 12),
    chip(96, 148, 56, 'culture1'),
    chip(160, 148, 64, 'culture2'),
    chip(232, 148, 48, 'culture3'),
    bar(96, 188, 232),
    bar(96, 204, 208),
    bar(96, 220, 176),
    bar(96, 256, 120, 'subtle'),
    bar(96, 272, 200),
    {
      type: 'rect',
      x: 440,
      y: 80,
      w: 136,
      h: 176,
      r: 8,
      fill: 'surface',
      stroke: 'line',
      strokeWidth: 1.5,
      rotate: 8,
    },
    {
      type: 'rect',
      x: 408,
      y: 96,
      w: 136,
      h: 184,
      r: 8,
      fill: 'surface',
      stroke: 'line',
      strokeWidth: 1.5,
      lift: true,
    },
    bar(424, 120, 80, 'subtle', 12),
    bar(424, 148, 104),
    bar(424, 164, 88),
    bar(424, 180, 96),
    { type: 'rect', x: 424, y: 240, w: 48, h: 24, r: 8, fill: 'accent' },
    circle(544, 280, 24, 'accent'),
    {
      type: 'path',
      d: 'M544 268 V290 M535 282 L544 291 L553 282',
      stroke: 'surface',
      strokeWidth: 3,
    },
  ],

  // Achievements and a score trend over terms
  collections: [
    lifted(card(80, 48, 480, 264)),
    ...[
      ['culture1', 136],
      ['culture2', 208],
      ['culture3', 280],
    ].flatMap(([tone, cx]): IllustrationShape[] => [
      circle(cx as number, 112, 28, 'accentSoft'),
      circle(cx as number, 112, 14, tone as IllustrationTone),
    ]),
    bar(344, 96, 168, 'subtle', 12),
    bar(344, 120, 136),
    { type: 'path', d: 'M120 280 H520', stroke: 'line', strokeWidth: 1.5 },
    {
      type: 'path',
      d: 'M136 256 L232 232 L328 240 L424 196 L504 176',
      stroke: 'accent',
      strokeWidth: 3,
    },
    ...[
      [136, 256],
      [232, 232],
      [328, 240],
      [424, 196],
      [504, 176],
    ].map(
      ([cx, cy]): IllustrationShape => ({
        type: 'circle',
        cx,
        cy,
        r: 6,
        fill: 'surface',
        stroke: 'accent',
        strokeWidth: 3,
      })
    ),
  ],

  // The activity panel with who sent what
  activity: [
    lifted(card(160, 64, 320, 256)),
    bar(184, 88, 96, 'subtle', 12),
    ...[140, 188, 236, 284].flatMap((y, i): IllustrationShape[] => [
      circle(
        196,
        y,
        16,
        ([
          'accentMild',
          'culture1',
          'culture2',
          'culture3',
        ] as IllustrationTone[])[i]
      ),
      bar(224, y - 10, [152, 120, 168, 136][i], i < 2 ? 'subtle' : 'muted'),
      bar(224, y + 4, [96, 128, 80, 112][i]),
      ...(i < 2 ? [circle(452, y, 5, 'accent')] : []),
    ]),
    circle(496, 64, 32, 'accent'),
    {
      type: 'path',
      d:
        'M484 72 V62 A12 12 0 0 1 508 62 V72 L512 76 H480 Z M492 80 A4 4 0 0 0 500 80',
      stroke: 'surface',
      strokeWidth: 2.5,
    },
    circle(516, 44, 8, 'culture3'),
  ],

  // The activity panel as an inbox: two tabs, an open request up front
  activityInbox: [
    card(160, 48, 320, 272),
    bar(184, 72, 64, 'subtle'),
    { type: 'rect', x: 184, y: 90, w: 64, h: 3, r: 1.5, fill: 'accent' },
    bar(272, 72, 64),
    { type: 'path', d: 'M160 100 H480', stroke: 'line', strokeWidth: 1.5 },
    lifted(card(176, 116, 288, 56, true)),
    circle(204, 144, 14, 'accentSoft'),
    bar(228, 134, 128, 'subtle'),
    bar(228, 148, 88),
    { type: 'rect', x: 400, y: 136, w: 48, h: 16, r: 8, fill: 'accent' },
    ...[204, 260].flatMap((y, i): IllustrationShape[] => [
      circle(204, y, 14, (['culture1', 'culture2'] as IllustrationTone[])[i]),
      bar(228, y - 10, [136, 112][i], 'muted'),
      bar(228, y + 4, [96, 120][i]),
      chip(400, y - 8, 48, 'muted'),
    ]),
    circle(496, 64, 32, 'accent'),
    {
      type: 'path',
      d:
        'M484 72 V62 A12 12 0 0 1 508 62 V72 L512 76 H480 Z M492 80 A4 4 0 0 0 500 80',
      stroke: 'surface',
      strokeWidth: 2.5,
    },
    circle(520, 40, 10, 'culture3'),
  ],

  // The public site's culture page with a fanned deck of values
  employerBrandingCulture: [
    card(64, 32, 512, 296),
    { type: 'rect', x: 64, y: 32, w: 512, h: 36, r: 12, fill: 'muted' },
    circle(88, 50, 5, 'subtle'),
    circle(104, 50, 5, 'subtle'),
    circle(120, 50, 5, 'subtle'),
    bar(144, 44, 200, 'surface', 12),
    bar(104, 104, 200, 'accent', 16),
    bar(104, 136, 160, 'accentMild'),
    bar(104, 156, 184),
    bar(104, 172, 136),
    chip(104, 208, 64),
    chip(176, 208, 56, 'muted'),
    chip(240, 208, 72, 'muted'),
    {
      type: 'rect',
      x: 384,
      y: 100,
      w: 120,
      h: 168,
      r: 12,
      fill: 'culture3',
      rotate: -10,
    },
    {
      type: 'rect',
      x: 448,
      y: 100,
      w: 120,
      h: 168,
      r: 12,
      fill: 'culture2',
      rotate: 10,
    },
    lifted({
      type: 'rect',
      x: 416,
      y: 88,
      w: 120,
      h: 176,
      r: 12,
      fill: 'surface',
      stroke: 'accent',
      strokeWidth: 2,
    }),
    circle(476, 136, 24, 'culture1'),
    bar(444, 180, 64, 'subtle'),
    bar(444, 196, 48),
    bar(444, 212, 56),
    { type: 'rect', x: 64, y: 296, w: 512, h: 32, r: 12, fill: 'muted' },
    bar(104, 308, 96, 'subtle'),
    bar(456, 308, 80, 'subtle'),
  ],

  // A public site with a hero and job cards
  employerBranding: [
    card(64, 32, 512, 296),
    { type: 'rect', x: 64, y: 32, w: 512, h: 36, r: 12, fill: 'muted' },
    circle(88, 50, 5, 'subtle'),
    circle(104, 50, 5, 'subtle'),
    circle(120, 50, 5, 'subtle'),
    bar(144, 44, 200, 'surface', 12),
    { type: 'rect', x: 88, y: 88, w: 464, h: 96, r: 8, fill: 'accentSoft' },
    bar(112, 116, 208, 'accent', 16),
    bar(112, 144, 152, 'accentMild'),
    { type: 'rect', x: 456, y: 124, w: 72, h: 24, r: 8, fill: 'accent' },
    ...[88, 248, 408].flatMap((x, i): IllustrationShape[] => [
      i === 1 ? lifted(card(x, 204, 144, 100, true)) : card(x, 204, 144, 100),
      {
        type: 'rect',
        x: x + 16,
        y: 220,
        w: 24,
        h: 24,
        r: 8,
        fill: (['culture1', 'culture2', 'culture3'] as IllustrationTone[])[i],
      },
      bar(x + 16, 260, 96, 'subtle'),
      bar(x + 16, 276, 72),
    ]),
  ],

  // A growth path as a matrix: jobs across, skills down
  jobMatrix: [
    { type: 'rect', x: 72, y: 152, w: 496, h: 60, r: 12, fill: 'accentSoft' },
    ...[88, 208, 328, 448].flatMap((x): IllustrationShape[] => [
      card(x, 48, 104, 44),
      bar(x + 16, 66, 64, 'subtle'),
      ...[104, 160, 216, 272].map(
        (y, row): IllustrationShape =>
          x === 208 && row === 1
            ? lifted(card(x, y, 104, 44, true))
            : card(x, y, 104, 44)
      ),
      ...[104, 160, 216, 272].map(
        (y, row): IllustrationShape =>
          bar(x + 16, y + 18, [72, 56, 64, 48][(row + x / 8) % 4])
      ),
    ]),
    circle(284, 178, 3, 'accent'),
    circle(292, 178, 3, 'accent'),
    circle(284, 186, 3, 'accent'),
    circle(292, 186, 3, 'accent'),
  ],

  // A results document with averages and a nine-grid
  teamResults: [
    card(96, 32, 448, 296),
    bar(128, 64, 160, 'subtle', 16),
    bar(128, 92, 104),
    { type: 'path', d: 'M128 288 H280', stroke: 'line', strokeWidth: 1.5 },
    { type: 'rect', x: 144, y: 208, w: 28, h: 80, r: 4, fill: 'accentMild' },
    { type: 'rect', x: 188, y: 168, w: 28, h: 120, r: 4, fill: 'accent' },
    { type: 'rect', x: 232, y: 192, w: 28, h: 96, r: 4, fill: 'accentMild' },
    ...[0, 1, 2].flatMap((row) =>
      [0, 1, 2].map(
        (col): IllustrationShape => ({
          type: 'rect',
          x: 320 + col * 64,
          y: 128 + row * 56,
          w: 56,
          h: 48,
          r: 8,
          fill: row === 0 && col === 2 ? 'surface' : 'muted',
          lift: row === 0 && col === 2,
        })
      )
    ),
    circle(472, 148, 7, 'accent'),
    circle(488, 164, 7, 'culture1'),
    circle(404, 208, 7, 'culture2'),
    circle(348, 264, 7, 'culture3'),
    circle(420, 256, 7, 'accentMild'),
  ],

  // The "What's new" dialog itself
  whatsNew: [
    card(96, 40, 448, 280),
    { type: 'rect', x: 96, y: 40, w: 152, h: 280, r: 12, fill: 'accentSoft' },
    ...[64, 104, 144, 184].flatMap((y, i): IllustrationShape[] => [
      i === 0
        ? {
            type: 'rect',
            x: 112,
            y,
            w: 120,
            h: 32,
            r: 8,
            fill: 'surface',
            lift: true,
          }
        : { type: 'rect', x: 112, y, w: 120, h: 32, r: 8, fill: 'accentSoft' },
      bar(124, y + 12, [72, 64, 80, 56][i], i === 0 ? 'subtle' : 'muted'),
      ...(i < 2 ? [circle(220, y + 16, 4, 'accent')] : []),
    ]),
    { type: 'rect', x: 272, y: 64, w: 248, h: 96, r: 8, fill: 'accentSoft' },
    circle(396, 112, 20, 'accent'),
    bar(272, 184, 200, 'subtle', 16),
    bar(272, 216, 232),
    bar(272, 232, 208),
    bar(272, 248, 176),
    { type: 'rect', x: 448, y: 280, w: 72, h: 24, r: 8, fill: 'accent' },
  ],

  // A mail in the company's colours with one button, and the switch beside it
  mails: [
    card(160, 40, 288, 280),
    { type: 'rect', x: 160, y: 40, w: 288, h: 64, r: 12, fill: 'accentSoft' },
    circle(200, 72, 16, 'accent'),
    bar(184, 128, 160, 'subtle', 16),
    bar(184, 160, 232),
    bar(184, 176, 208),
    bar(184, 192, 176),
    lifted({
      type: 'rect',
      x: 184,
      y: 224,
      w: 112,
      h: 32,
      r: 8,
      fill: 'accent',
    }),
    bar(184, 280, 120),
    card(472, 136, 104, 56),
    { type: 'rect', x: 492, y: 152, w: 40, h: 24, r: 12, fill: 'accent' },
    circle(520, 164, 8, 'surface'),
    bar(540, 160, 24),
  ],
}

export default releaseNoteIllustrations
